<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { injectAccountModules } from 'dashboard/helpers/accountModules';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const getters = useStoreGetters();
const config = computed(() => getters['crmConfig/getCrmConfig'].value);

const modules = ref([]);
const expandedModule = ref(null);
const newModuleName = ref('');
const newModuleType = ref('script');
const showAddForm = ref(false);
const saving = ref(false);
const loading = ref(true);

const toggleModule = index => {
  expandedModule.value = expandedModule.value === index ? null : index;
};

const addModule = () => {
  if (!newModuleName.value.trim()) return;
  modules.value.push({
    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
    name: newModuleName.value.trim(),
    type: newModuleType.value,
    code: '',
    active: true,
  });
  newModuleName.value = '';
  newModuleType.value = 'script';
  showAddForm.value = false;
  expandedModule.value = modules.value.length - 1;
};

const removeModule = index => {
  modules.value.splice(index, 1);
  if (expandedModule.value === index) expandedModule.value = null;
};

const toggleModuleActive = index => {
  modules.value[index].active = !modules.value[index].active;
};

const onSave = async () => {
  saving.value = true;
  try {
    const currentConfig = config.value?.config || {};
    await store.dispatch('crmConfig/update', {
      crm_config: {
        config: {
          ...currentConfig,
          modules: modules.value,
        },
      },
    });
    injectAccountModules(modules.value);
  } finally {
    saving.value = false;
  }
};

onMounted(async () => {
  await store.dispatch('crmConfig/get');
  if (config.value?.config?.modules) {
    modules.value = JSON.parse(JSON.stringify(config.value.config.modules));
  }
  loading.value = false;
});
</script>

<template>
  <SettingsLayout :is-loading="loading" loading-message="Carregando scripts...">
    <template #header>
      <BaseSettingsHeader
        title="Scripts"
        description="Gerencie scripts e estilos customizados para esta conta. Cada script e injetado no dashboard quando ativo."
      />
    </template>
    <template #body>
      <div class="flex-grow flex-shrink overflow-auto">
        <!-- Toolbar -->
        <div class="flex items-center justify-between mb-6">
          <p class="text-sm text-n-slate-11">
            {{ modules.length }} script{{ modules.length !== 1 ? 's' : '' }} cadastrado{{ modules.length !== 1 ? 's' : '' }}
          </p>
          <div class="flex gap-2">
            <Button
              label="Salvar"
              color-scheme="primary"
              :is-loading="saving"
              @click="onSave"
            />
            <Button
              label="+ Adicionar Script"
              color-scheme="primary"
              variant="faded"
              @click="showAddForm = true"
            />
          </div>
        </div>

        <!-- Add form -->
        <div
          v-if="showAddForm"
          class="mb-4 p-4 outline outline-1 outline-n-container bg-n-alpha-3 rounded-lg"
        >
          <div class="flex items-end gap-3">
            <div class="flex-1">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Nome</label>
              <input
                v-model="newModuleName"
                type="text"
                class="w-full px-3 py-2 border border-n-weak bg-n-alpha-2 text-n-slate-12 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
                placeholder="Nome do script"
                @keyup.enter="addModule"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Tipo</label>
              <select
                v-model="newModuleType"
                class="px-3 py-2 border border-n-weak bg-n-alpha-2 text-n-slate-12 rounded-lg text-sm"
              >
                <option value="script">Script (JS)</option>
                <option value="style">Estilo (CSS)</option>
                <option value="function">Funcao</option>
              </select>
            </div>
            <Button label="Criar" color-scheme="primary" @click="addModule" />
            <Button label="Cancelar" variant="faded" @click="showAddForm = false" />
          </div>
        </div>

        <!-- Empty state -->
        <div
          v-if="modules.length === 0 && !showAddForm"
          class="flex flex-col items-center justify-center py-16 text-n-slate-10"
        >
          <i class="i-lucide-file-code-2 text-4xl mb-3" />
          <p class="text-sm">Nenhum script cadastrado.</p>
          <p class="text-xs mt-1">Clique em "+ Adicionar Script" para começar.</p>
        </div>

        <!-- Scripts list -->
        <div class="flex flex-col gap-2">
          <div v-for="(mod, index) in modules" :key="mod.id || index">
            <div
              class="flex items-center justify-between px-4 py-3 rounded-lg outline outline-1 transition cursor-pointer"
              :class="
                mod.active
                  ? 'outline-n-container bg-n-alpha-3'
                  : 'outline-n-container/50 bg-n-alpha-1 opacity-60'
              "
              @click="toggleModule(index)"
            >
              <div class="flex items-center gap-3">
                <i
                  class="text-n-slate-10"
                  :class="expandedModule === index ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
                />
                <span class="text-sm font-medium text-n-slate-12">{{ mod.name }}</span>
                <span
                  class="text-[10px] px-1.5 py-0.5 rounded font-mono"
                  :class="{
                    'bg-n-amber-3 text-n-amber-11': mod.type === 'script' || mod.type === 'function',
                    'bg-n-blue-3 text-n-blue-11': mod.type === 'style',
                  }"
                >
                  {{ mod.type === 'style' ? 'CSS' : 'JS' }}
                </span>
                <span v-if="mod.code" class="text-[10px] text-n-slate-10">
                  {{ mod.code.length }} chars
                </span>
              </div>
              <div class="flex items-center gap-3" @click.stop>
                <label class="text-xs text-n-slate-11 flex items-center gap-1.5 cursor-pointer">
                  Ativo
                  <input
                    type="checkbox"
                    :checked="mod.active"
                    class="accent-n-brand"
                    @change="toggleModuleActive(index)"
                  />
                </label>
                <button
                  class="text-n-ruby-10 hover:text-n-ruby-11 p-1"
                  title="Remover"
                  @click="removeModule(index)"
                >
                  <i class="i-lucide-trash-2 text-sm" />
                </button>
              </div>
            </div>
            <!-- Expanded: code editor -->
            <div v-if="expandedModule === index" class="mt-1 ml-7 mr-1">
              <div class="mb-2 flex items-center gap-2">
                <input
                  v-model="mod.name"
                  type="text"
                  class="flex-1 px-3 py-1.5 border border-n-weak bg-n-alpha-2 text-n-slate-12 rounded text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
                  placeholder="Nome do script"
                  @click.stop
                />
                <select
                  v-model="mod.type"
                  class="px-2 py-1.5 border border-n-weak bg-n-alpha-2 text-n-slate-12 rounded text-sm"
                  @click.stop
                >
                  <option value="script">Script (JS)</option>
                  <option value="style">Estilo (CSS)</option>
                  <option value="function">Funcao</option>
                </select>
              </div>
              <textarea
                v-model="mod.code"
                class="w-full h-64 px-3 py-2 font-mono text-xs border border-n-weak bg-n-alpha-1 text-n-slate-12 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-n-brand"
                placeholder="Cole o codigo aqui..."
                spellcheck="false"
                @click.stop
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
